import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useThrottledMutation as useMutation } from '@hooks/use-throttled-mutation';

import {
  createJourneyWebhook,
  deleteJourneyWebhook,
  JourneyWebhook,
  JourneyWebhookData,
  JourneyWebhookList,
  loadJourneyWebhook,
  loadJourneyWebhookLog,
  loadJourneyWebhookLogs,
  loadJourneyWebhooks,
  updateJourneyWebhook,
} from './requests';

const getJourneyWebhooksKey = (journeyShortId: string) => ['journey', journeyShortId, 'webhooks'];
const getJourneyWebhookKey = (webhookShortId: string) => ['journey', webhookShortId, 'webhook'];

const useInvalidateJourneyWebhooks = () => {
  const queryClient = useQueryClient();

  const invalidateJourneyWebhooks = (shortId: string) =>
    queryClient.invalidateQueries(getJourneyWebhooksKey(shortId));

  const invalidateJourneyWebhook = (webhookShortId: string) =>
    queryClient.invalidateQueries(getJourneyWebhookKey(webhookShortId));

  return { invalidateJourneyWebhooks, invalidateJourneyWebhook };
};

export const useJourneyWebhooksList = (journeyShortId: string) => {
  return useQuery({
    queryKey: getJourneyWebhooksKey(journeyShortId),
    queryFn: () => loadJourneyWebhooks(journeyShortId),
  });
};

export const useJourneyWebhook = (webhookShortId: string) => {
  return useQuery({
    queryKey: getJourneyWebhookKey(webhookShortId),
    queryFn: () => loadJourneyWebhook(webhookShortId),
  });
};

export const useCreateJourneyWebhook = (journeyShortId: string) => {
  const { invalidateJourneyWebhooks } = useInvalidateJourneyWebhooks();
  return useMutation({
    mutationFn: () => createJourneyWebhook(journeyShortId),
    onSuccess: () => {
      invalidateJourneyWebhooks(journeyShortId);
    },
  });
};

export const useUpdateJourneyWebhook = (webhook: JourneyWebhook | JourneyWebhookList) => {
  const { invalidateJourneyWebhooks, invalidateJourneyWebhook } = useInvalidateJourneyWebhooks();
  return useMutation({
    mutationFn: (webhookData: Partial<JourneyWebhookData>) =>
      updateJourneyWebhook(webhook.shortId, webhookData),
    onSuccess: () => {
      invalidateJourneyWebhook(webhook.shortId);
      invalidateJourneyWebhooks(webhook.journeyShortId);
    },
  });
};

export const useDeleteJourneyWebhook = (journeyShortId: string) => {
  const { invalidateJourneyWebhooks } = useInvalidateJourneyWebhooks();
  return useMutation({
    mutationFn: (webhookShortId: string) => deleteJourneyWebhook(webhookShortId),
    onSuccess: () => {
      invalidateJourneyWebhooks(journeyShortId);
    },
  });
};

export const useJourneyWebhookLogs = (webhookShortId: string) => {
  return useQuery({
    queryKey: ['journeys', webhookShortId, 'webhook', 'logs'],
    queryFn: () => loadJourneyWebhookLogs(webhookShortId),
  });
};

export const useJourneyWebhookLog = (webhookShortId: string, webhookLogShortId: string) => {
  return useQuery({
    queryKey: ['journeys', webhookShortId, webhookLogShortId, 'webhook', 'log'],
    queryFn: () => loadJourneyWebhookLog(webhookShortId, webhookLogShortId),
  });
};
